<template>
  <div class="development-plots-component">

    <h4>Add House</h4>
    <div class="card card-body mb-4">
      <div class="row">

        <div class="col-12 col-md-6 col-lg-2">
          <div class="form-group">
            <label>House Name</label>
            <input
              v-model="plotName"
              type="text"
              class="form-control"
              placeholder="Name/Number"
              :disabled="loading"
            />
          </div>
        </div>

        <div class="col-12 col-md-6 col-lg-2">
          <div class="form-group">
            <label>From Price</label>
            <vue-autonumeric
              v-model.number="plotPrice"
              placeholder="Price"
              :disabled="loading"
              class="form-control"
              :options="[
                'British',
                {
                  minimumValue: '0',
                  allowDecimalPadding: true
                }
              ]"
            ></vue-autonumeric>
          </div>
        </div>

        <div class="col-12 col-md-2">
          <div class="form-group">
            <label for="house_type">House Type</label>
            <select
              v-model.number="developmentHouseTypeId"
              class="form-control"
              :disabled="loading"
            >
              <option :value="null">Select House Type</option>
              <option
                v-for="developmentHouseType in developmentHouseTypes"
                :key="developmentHouseType.id"
                :value="developmentHouseType.id"
              >
                {{ developmentHouseType.house_type.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="col-12 col-md-2">
          <div class="form-group">
            <label for="plot_status">House Status</label>
            <select
              v-model.number="plotStatus"
              class="form-control"
              id="plot_status"
              :disabled="loading"
            >
              <option :value="null">Select Status</option>
              <option
                v-for="(name, id) in plotStatuses"
                :key="id"
                :value="id"
              >
                {{ name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Add plot orientation -->
        <div class="col-12 col-sm-4 col-md-2">
          <div class="form-group">
            <label for="plot_has_standard_orientation" class="me-2 w-100">House Orientation</label>
            <select
              v-model="orientation"
              class="form-control"
              :disabled="loading"
            >
              <option :value="true">AS</option>
              <option :value="false">OPP</option>
            </select>
          </div>
        </div>

        <div class="col-12 col-md-2">
          <div class="form-group">
            <label>&nbsp;</label>
            <button
              @click="addPlotToDevelopment()"
              class="btn btn-success btn-block"
              type="button"
            >
              Add House
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showPlots">
      <div v-if="addedPlots.length > 0" class="row">
        <div class="col-12 col-xl-10">
          <h4>Added Homes ({{ addedPlots.length }})</h4>

          <div class="form-inline mb-3">
            <div class="form-group me-3">
              <label for="search" class="me-2">Search</label>
              <input
                type="text"
                class="form-control"
                id="search"
                placeholder="Start typing..."
                v-model="search">
            </div>

            <div v-if="search" class="form-group">
              <button @click="search = null" class="btn btn-danger me-3">Clear</button>
            </div>

            <div class="form-group me-3">
              <label for="status" class="me-2">Status</label>
              <select
                v-model.number="status"
                class="form-control"
                :disabled="loading"
              >
                <option :value="null">Any</option>
                <option
                  v-for="(name, id) in plotStatuses"
                  :key="id"
                  :value="id"
                >
                  {{ name }}
                </option>
              </select>
            </div>
          </div>

          <button
            @click="savePlots()"
            v-if="isDirty"
            type="button"
            class="btn btn-success my-3"
            :disabled="loading"
          >
            Save Houses
          </button>

          <small class="text-muted d-block mb-3">Showing {{ filteredPlots.length }} results.</small>

          <div
            v-for="plot in filteredPlots"
            :key="plot.id"
            class="card card-body mb-3"
          >
            <h5>
              <strong>House:</strong>
              {{ plot.name_number }}

              <span v-if="plot.build_status" class="text-muted">(Build Status: {{ plot.build_status.name}})</span>
            </h5>
            <div class="d-flex align-items-center">
              <div class="me-4 d-flex align-items-center">
                <label class="me-2 mb-0">Name</label>
                <input
                  v-model="plot.name_number"
                  type="text"
                  class="form-control"
                  placeholder="Name/Number"
                  :disabled="loading || !canDetails"
                />
              </div>

              <div class="me-4 d-flex align-items-center">
                <label class="me-2 mb-0">From Price</label>
                <vue-autonumeric
                  v-model.number="plot.price"
                  placeholder="Price"
                  :disabled="loading || !canDetails"
                  class="form-control"
                  :options="[
                    'British',
                    {
                      minimumValue: '0',
                      allowDecimalPadding: true
                    }
                  ]"
                ></vue-autonumeric>
              </div>

              <div class="me-4 d-flex align-items-center">
                <label for="house_type" class="me-2 mb-0">House Type</label>
                <select
                  v-model.number="plot.development_house_type_id"
                  class="form-control"
                  :disabled="loading || !canDetails"
                >
                  <option :value="null">Select House Type</option>
                  <option
                    v-for="developmentHouseType in developmentHouseTypes"
                    :key="developmentHouseType.id"
                    :value="developmentHouseType.id"
                  >
                    {{ developmentHouseType.house_type.name }}
                  </option>
                </select>
              </div>

              <div class="me-auto d-flex align-items-center">
                <label class="me-2 mb-0">Status</label>
                <select
                  v-model.number="plot.plot_status_id"
                  class="form-control"
                  :disabled="loading || !canDetails"
                >
                  <option
                    v-for="(name, id) in plotStatuses"
                    :key="id"
                    :value="id"
                  >
                    {{ name }}
                  </option>
                </select>
              </div>

              <!--            <div>-->
              <!--              <button-->
              <!--                @click="removePlot(plot.id)"-->
              <!--                type="button"-->
              <!--                class="btn btn-danger btn-sm"-->
              <!--              >-->
              <!--                <i class="far fa-trash text-white me-0"></i>-->
              <!--              </button>-->
              <!--            </div>-->
            </div>

            <hr>

            <span v-if="plot.target_build_completion || plot.target_contract_exchange || plot.target_legal_completion"
                  class="d-block mb-2">
              <strong>Target Dates:</strong>
              <span v-if="plot.target_build_completion" class="mx-2">Build Completion {{
                  plot.target_build_completion
                }}</span>
              <span v-if="plot.target_contract_exchange" class="mx-2">Contract Exchange {{
                  plot.target_contract_exchange | moment('L')
                }}</span>
              <span v-if="plot.target_legal_completion" class="mx-2">Legal Completion {{
                  plot.target_legal_completion | moment('L')
                }}</span>
            </span>

            <div class="d-flex flex-column">
              <div class="d-flex">
                <plot-assignment-component v-if="canAssign" :plot="plot" :development-id="developmentId" class="me-3"
                                           v-on:plots-updated="plotsUpdated"></plot-assignment-component>

                <early-bird-assignment-component v-if="canEarlyBird" :plot="plot" :development-id="developmentId"
                                                 v-on:plots-updated="plotsUpdated"></early-bird-assignment-component>
              </div>


              <div class="d-flex justify-content-between align-items-center">
                <div class="mt-2">
                  <a :href="`/admin/developments/${developmentId}/plots/${plot.id}`" class="btn btn-primary me-1"
                  >View
                    House</a>

                  <a :href="`/admin/developments/${developmentId}/plots/${plot.id}/delete`"
                     class="btn btn-danger btn-sm">Delete</a>
                </div>

                <!-- Edit plot orientation -->
                <div class="form-group d-flex align-items-center">
                  <label for="plot_has_standard_orientation" class="me-2 w-100">House Orientation</label>
                  <select
                    v-model.number="plot.has_standard_orientation"
                    class="form-control"
                    :disabled="loading"
                  >
                    <option :value="true">AS</option>
                    <option :value="false">OPP</option>
                  </select>
                </div>

                <a v-if="canDetails" :href="`/admin/developments/${developmentId}/plots/${plot.id}/delete`"
                   class="btn btn-danger btn-sm">Delete</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <small v-else class="text-muted">No plots found.</small>

      <button
        @click="savePlots()"
        v-if="isDirty"
        type="button"
        class="btn btn-success my-3"
        :disabled="loading"
      >
        Save Plots
      </button>

      <div v-if="loading" class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import PlotAssignmentComponent from "./PlotAssignmentComponent";
import EarlyBirdAssignmentComponent from "./EarlyBirdAssignmentComponent.vue";

export default {
  components: {EarlyBirdAssignmentComponent, PlotAssignmentComponent},
  data() {
    return {
      loading: false,
      plotName: null,
      plotPrice: 0,
      plotStatus: null,
      developmentHouseTypeId: null,
      addedPlots: [],
      isDirty: false,
      search: null,
      status: null,
      orientation: true,
    };
  },

  props: {
    developmentId: {
      type: Number
    },

    plotStatuses: {
      type: Object
    },

    developmentHouseTypes: {
      type: Array
    },

    preloadPlots: {
      type: Array
    },

    showPlots: {
      type: Boolean,
      default: true
    },

    canDetails: {
      type: Boolean,
    },

    canAssign: {
      type: Boolean,
    },

    canEarlyBird: {
      type: Boolean,
    },
  },

  watch: {
    addedPlots: {
      deep: true,

      handler(newData, oldData) {
        this.isDirty = !isEqual(this.preloadPlots, this.addedPlots);
      }
    }
  },

  computed: {
    filteredPlots() {
      if (!this.search && !this.status) {
        return this.addedPlots;
      }

      let plots = this.addedPlots;

      // Is there a search query?
      if (this.search) {
        const matchingDevelopmentHouseTypesIds = this.developmentHouseTypes.filter(
          (developmentHouseType) =>
            developmentHouseType.house_type.name.toLowerCase().includes(this.search.toLowerCase())
        ).map((developmentHouseType) => parseInt(developmentHouseType.id));

        plots = plots.filter(plot => {
          return plot.name_number.toLowerCase().includes(this.search.toLowerCase()) ||
            matchingDevelopmentHouseTypesIds.includes(plot.development_house_type_id) ||
            plot.build && plot.build.user && plot.build.user.name.toLowerCase().includes(this.search.toLowerCase()) ||
            plot.build && plot.build.user && plot.build.user.email.toLowerCase().includes(this.search.toLowerCase());
        });
      }

      // Is there a status?
      if (this.status) {
        plots = plots.filter(plot => plot.plot_status_id === this.status);
      }

      return plots;
    },
  },

  mounted() {
    // We use JSON, so we get a clone and not a reference.
    if (this.showPlots) {
      this.addedPlots = JSON.parse(JSON.stringify(this.preloadPlots));
    }

    // Is there a status get parameter?
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const status = urlParams.get('status');

    if (status) {
      const statusId = Object.entries(this.plotStatuses).find(([id, name]) => name.toLowerCase() === status.toLowerCase());

      if (statusId) {
        this.status = parseInt(statusId[0]);
      }
    }

    this.$nextTick(() => {
      this.isDirty = false;
    });
  },

  methods: {
    async addPlotToDevelopment() {
      this.loading = true;

      if (this.isDirty) {
        await this.savePlots();
      }

      return axios
        .post(
          `${window.api_url}/admin/developments/add_plot/${this.developmentId}`,
          {
            name: this.plotName,
            status: this.plotStatus,
            price: this.plotPrice,
            development_house_type_id: this.developmentHouseTypeId,
            has_standard_orientation: this.orientation,
          }
        )
        .then(response => {
          this.loading = false;

          if (response.data.success) {
            notyf.success(`Plot ${this.plotName} successfully added.`);

            this.addedPlots = response.data.plots;
            this.plotName = null;
            this.plotStatus = null;
            this.development_house_type_id = null;
            this.plotPrice = 0;
            this.orientation = true;

            // fire a native event to tell livewire table to update
            window.dispatchEvent(new Event('added-plot'));
          } else {
            notyf.error("Something went wrong. Please try again.");
          }
        })
        .catch(error => {
          this.loading = false;
          notyf.error("Something went wrong. Please try again.");
        });
    },

    savePlots() {
      this.loading = true;

      return axios
        .post(
          `${window.api_url}/admin/developments/save_plots/${this.developmentId}`,
          {
            plots: this.addedPlots
          }
        )
        .then(response => {
          this.loading = false;

          if (response.data.success) {
            this.isDirty = false;
            this.addedPlots = response.data.plots;
            notyf.success("Plots successfully saved.");
          } else {
            notyf.error("Something went wrong. Please try again.");
          }
        })
        .catch(error => {
          this.loading = false;
          notyf.error("Something went wrong. Please try again.");
        });
    },

    removePlot(plotId) {
      // Currently disabled functionality.
      return;

      if (!confirm('Are you sure you want to remove this plot?')) {
        return;
      }

      this.addedPlots = this.addedPlots.filter(
        plot => plot.id !== plotId
      );
    },

    plotsUpdated(plots) {
      this.addedPlots = plots;
    }
  }
};
</script>
